// CROSSSELL-109 — Store V2 product title resolver (UA default / EN optional).
// No Russian UI labels. Brands preserved. SKU/barcode/weight untouched.

import { PRODUCT_TITLE_OVERRIDES, type TitleOverride } from "./product-title-overrides.js";

export type StoreV2Lang = "ua" | "en";

export interface TitleResolveInput {
  id: string;
  sku?: string | null;
  title?: string | null;
  titleRu?: string | null;
  titleUa?: string | null;
  titleEn?: string | null;
  brand?: string | null;
  categoryName?: string | null;
  categoryKeys?: string[];
}

export interface TitleResolveResult {
  title: string;
  lang: StoreV2Lang;
  source: "field" | "override" | "generated" | "fallback";
  needsTranslation: boolean;
  confidence: "high" | "medium" | "low";
}

const OVERRIDES: Record<string, TitleOverride> = PRODUCT_TITLE_OVERRIDES;

/** Common RU → UA food/commerce lemmas. Brand names never listed. */
const RU_TO_UA: Array<[RegExp, string]> = [
  [/Лапша/gi, "Локшина"],
  [/лапша/gi, "локшина"],
  [/Масло/gi, "Олія"],
  [/масло/gi, "олія"],
  [/Чипсы/gi, "Чіпси"],
  [/чипсы/gi, "чіпси"],
  [/Вафли/gi, "Вафлі"],
  [/вафли/gi, "вафлі"],
  [/Смесь/gi, "Суміш"],
  [/смесь/gi, "суміш"],
  [/быстрого приготовления/gi, "швидкого приготування"],
  [/со вкусом/gi, "зі смаком"],
  [/органическое/gi, "органічне"],
  [/органическая/gi, "органічна"],
  [/органический/gi, "органічний"],
  [/органические/gi, "органічні"],
  [/некрафинированное/gi, "нерафіноване"],
  [/некрафинированная/gi, "нерафінована"],
  [/холодного отжима/gi, "холодного віджиму"],
  [/жареного/gi, "смаженого"],
  [/жареных/gi, "смажених"],
  [/для жарки/gi, "для смаження"],
  [/низкокалорийное/gi, "низькокалорійне"],
  [/низкокалорийная/gi, "низькокалорійна"],
  [/Макаронные изделия/gi, "Макаронні вироби"],
  [/Консервированные/gi, "Консервовані"],
  [/фрукты и ягоды/gi, "фрукти та ягоди"],
  [/Диетические продукты/gi, "Дієтичні продукти"],
  [/Растительное масло/gi, "Рослинна олія"],
  [/Большая Британия/gi, "Велика Британія"],
  [/Германия/gi, "Німеччина"],
  [/Франция/gi, "Франція"],
  [/Италия/gi, "Італія"],
  [/Польша/gi, "Польща"],
  [/Япония/gi, "Японія"],
  [/Нидерланды/gi, "Нідерланди"],
  [/Австрия/gi, "Австрія"],
  [/азиатской кухни/gi, "азійської кухні"],
  [/вкусом/gi, "смаком"],
];

const CYRILLIC = /[\u0400-\u04FF]/;
const UK_MARKERS = /[іїєґІЇЄҐ]/;
const RU_MARKERS = /[ыэёъЫЭЁЪ]/;
const LATIN = /[A-Za-z]/;

function splitPipeParts(source: string): string[] {
  return source
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);
}

function looksPrimarilyLatin(s: string): boolean {
  const letters = s.replace(/[^A-Za-z\u0400-\u04FF]/g, "");
  if (!letters) return false;
  const latin = (letters.match(/[A-Za-z]/g) ?? []).length;
  return latin / letters.length >= 0.6;
}

function applyRuToUa(text: string): string {
  let out = text;
  for (const [re, repl] of RU_TO_UA) {
    out = out.replace(re, repl);
  }
  return out.replace(/\s{2,}/g, " ").trim();
}

function detectSourceLang(title: string): "ua" | "ru" | "en" | "mixed" | "unknown" {
  if (!title.trim()) return "unknown";
  if (!CYRILLIC.test(title) && LATIN.test(title)) return "en";
  const hasUk = UK_MARKERS.test(title);
  const hasRu = RU_MARKERS.test(title);
  if (hasUk && !hasRu) return "ua";
  if (hasRu && !hasUk) return "ru";
  if (CYRILLIC.test(title)) return hasUk || hasRu ? "mixed" : "ru";
  return "unknown";
}

function overrideFor(input: TitleResolveInput): TitleOverride | null {
  const byId = OVERRIDES[input.id];
  if (byId) return byId;
  const sku = String(input.sku ?? "").trim();
  if (sku && OVERRIDES[sku]) return OVERRIDES[sku];
  return null;
}

function generateUa(input: TitleResolveInput, source: string): { title: string; confidence: TitleResolveResult["confidence"] } {
  const parts = splitPipeParts(source);
  const cyrPart = parts.find((p) => CYRILLIC.test(p)) ?? parts[0] ?? source;
  let base = applyRuToUa(cyrPart);
  const brand = input.brand?.trim();
  if (brand && !base.toLowerCase().includes(brand.toLowerCase())) {
    base = `${base} · ${brand}`;
  }
  const confidence =
    detectSourceLang(cyrPart) === "ua" ? "high" : UK_MARKERS.test(base) || base !== cyrPart ? "medium" : "low";
  return { title: base, confidence };
}

function generateEn(input: TitleResolveInput, source: string): { title: string; confidence: TitleResolveResult["confidence"] } {
  const parts = splitPipeParts(source);
  const latinPart = parts.find((p) => looksPrimarilyLatin(p));
  if (latinPart) {
    const brand = input.brand?.trim();
    let title = latinPart;
    if (brand && !title.toLowerCase().includes(brand.toLowerCase())) {
      title = `${brand} · ${title}`;
    }
    return { title, confidence: "medium" };
  }
  // Last resort: keep source but strip heavy RU pipe head if EN fragment exists mid-string
  const brand = input.brand?.trim();
  if (brand) return { title: `${brand} · ${source.slice(0, 80)}`, confidence: "low" };
  return { title: source, confidence: "low" };
}

/**
 * Resolve display title for UA or EN. Never invents weight/SKU; never uses RU UI labels.
 */
export function resolveProductTitle(input: TitleResolveInput, lang: StoreV2Lang = "ua"): TitleResolveResult {
  const source = String(input.title ?? input.titleRu ?? "").trim();

  if (lang === "ua") {
    const field = String(input.titleUa ?? "").trim();
    if (field) {
      return { title: field, lang, source: "field", needsTranslation: false, confidence: "high" };
    }
    const ov = overrideFor(input);
    if (ov?.ua?.trim()) {
      return { title: ov.ua.trim(), lang, source: "override", needsTranslation: false, confidence: "high" };
    }
    if (source) {
      const gen = generateUa(input, source);
      const detected = detectSourceLang(source);
      const needsTranslation = detected === "ru" || detected === "mixed" || detected === "unknown";
      return {
        title: gen.title,
        lang,
        source: "generated",
        needsTranslation,
        confidence: gen.confidence,
      };
    }
    return { title: input.sku || input.id || "Товар", lang, source: "fallback", needsTranslation: true, confidence: "low" };
  }

  // EN
  const fieldEn = String(input.titleEn ?? "").trim();
  if (fieldEn) {
    return { title: fieldEn, lang, source: "field", needsTranslation: false, confidence: "high" };
  }
  const ov = overrideFor(input);
  if (ov?.en?.trim()) {
    return { title: ov.en.trim(), lang, source: "override", needsTranslation: false, confidence: "high" };
  }
  if (source) {
    const gen = generateEn(input, source);
    return {
      title: gen.title,
      lang,
      source: "generated",
      needsTranslation: gen.confidence !== "medium",
      confidence: gen.confidence,
    };
  }
  return { title: input.sku || input.id || "Product", lang, source: "fallback", needsTranslation: true, confidence: "low" };
}

export function detectTitleLanguage(sourceTitle: string): ReturnType<typeof detectSourceLang> {
  return detectSourceLang(sourceTitle);
}

export function countOverrides(): number {
  return Object.keys(OVERRIDES).length;
}
